import { UserMemberProfileDTO } from "@gymflow/types";
import { FormikProvider, useFormik } from "formik";
import { useMutationMemberProfile } from "libs/api/src/lib/hooks/member/useMutationMemberProfile";
import { useMutationMemberProfilePicture } from "libs/api/src/lib/hooks/member/useMutationMemberProfilePicture";
import { useQueryMemberProfile } from "libs/api/src/lib/hooks/member/useQueryMemberProfile";
import { MemberSchemaHelper } from "libs/validation/src/MemberSchema";
import { omit } from "lodash";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { useClubSettings } from "../../../providers/settings";
import useGymflowModels from "../../../store";
import { Button, Spinner } from "../../atoms";
import { FormImageInput } from "../../atoms/form/FormImageInput";
import { FormInput } from "../../atoms/form/FormInput";
import { FormPhoneNumberInput } from "../../atoms/form/FormPhoneNumberInput";
import { FormRadioButtonGroup } from "../../atoms/form/FormRadioButtonGroup";
import { FormSingleDatePicker } from "../../atoms/form/FormSingleDatePicker";

export const MemberForm = ({ memberId }: { memberId: string }) => {
  const { api } = useGymflowModels();
  const { data: member, isFetching } = useQueryMemberProfile({
    api,
    memberId,
  });
  if (isFetching || !member) {
    return <Spinner />;
  }
  return <LoadedMemberForm member={member} />;
};

export const LoadedMemberForm = ({
  member,
}: {
  member: UserMemberProfileDTO;
}) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const settings = useClubSettings();

  const { mutateAsync: updateMember } = useMutationMemberProfile({ api });
  const { mutateAsync: updateMemberPicture } = useMutationMemberProfilePicture({
    api,
  });

  const schema = useMemo(
    () =>
      MemberSchemaHelper.schema(
        settings.propertiesSettings.properties.postalCodeCountry,
      ),
    [settings.propertiesSettings.properties.postalCodeCountry],
  );

  const initialValues = useMemo(
    () => ({
      ...MemberSchemaHelper.defaultValues,
      ...member,
    }),
    [member],
  );
  const formik = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(schema),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const toSubmit = omit(values, "picture");
      await updateMember({
        memberId: member.userMemberId,
        profile: toSubmit,
      });

      if (values.picture !== member?.picture) {
        await updateMemberPicture({
          memberId: member.userMemberId,
          pictureUrl: values.picture as string,
        });
      }
    },
  });
  const parent = useRef<HTMLDivElement>(null);
  return (
    <FormikProvider value={formik}>
      <div ref={parent} className="flex w-full flex-col items-center gap-6 p-6">
        <FormImageInput
          name="picture"
          modalTitle={t("pages.member.profile.picture")}
          placeholder={`${member?.firstName?.[0] || ""}${member?.lastName?.[0] || ""}`}
          containerClassName="w-full items-center justify-center"
        />
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
          <FormInput
            name="firstName"
            label={t("pages.member.profile.firstName")}
            placeholder={t("pages.member.profile.firstName")}
          />
          <FormInput
            name="lastName"
            label={t("pages.member.profile.lastName")}
            placeholder={t("pages.member.profile.lastName")}
          />
          <FormPhoneNumberInput
            name="mobileNumber"
            label={t("pages.member.profile.mobilePhone")}
            placeholder={t("pages.member.profile.mobilePhone")}
          />

          <FormSingleDatePicker
            name="dateBirth"
            label={t("pages.member.profile.dateOfBirth")}
            useDateOnly
            useIsoString
            hideNavigation
            captionLayout="dropdown"
          />

          <FormRadioButtonGroup
            name="gender"
            label={t("pages.member.profile.gender")}
            options={[
              {
                value: "MALE",
                label: t("pages.member.profile.MALE"),
              },
              {
                value: "FEMALE",
                label: t("pages.member.profile.FEMALE"),
              },
              {
                value: "PREFER_NOT_TO_SAY",
                label: t("pages.member.profile.PREFER_NOT_TO_SAY"),
              },
            ]}
            className="flex-wrap"
            labelClassName="text-sm"
          />

          <FormInput
            name="personalNumber"
            label={t("pages.member.profile.personalNumber")}
            placeholder={t("pages.member.profile.personalNumber")}
          />

          <FormInput
            name="addressLine1"
            label={t("pages.member.profile.addressLine1")}
            placeholder={t("pages.member.profile.addressLine1")}
          />

          <FormInput
            name="addressLine2"
            label={t("pages.member.profile.addressLine2")}
            placeholder={t("pages.member.profile.addressLine2")}
          />

          <FormInput
            name="city"
            label={t("pages.member.profile.city")}
            placeholder={t("pages.member.profile.city")}
          />

          <FormInput
            name="postCode"
            label={t("pages.member.profile.postCode")}
            placeholder={t("pages.member.profile.postCode")}
          />

          <FormInput
            name="emergencyContactName"
            label={t("pages.member.profile.emergencyContactName")}
            placeholder={t("pages.member.profile.emergencyContactName")}
          />

          <FormPhoneNumberInput
            name="emergencyContact"
            label={t("pages.member.profile.emergencyContact")}
            placeholder={t("pages.member.profile.emergencyContact")}
          />

          <FormInput
            name="customInvoiceField"
            label={t("pages.member.profile.customInvoiceField")}
            placeholder={t("pages.member.profile.customInvoiceField")}
          />
        </div>
        <div className="flex w-full justify-end border-t border-gray-200 pt-4">
          <Button
            intent="secondary"
            onClick={async () => await formik.submitForm()}
            disabled={formik.isSubmitting || !formik.dirty}
            className="min-w-[120px]"
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};
